<template>
  <article class="sd-page pc">
    <!-- Aviso: página aún no publicada (solo se llega por su URL o en desarrollo) -->
    <p v-if="!publicada" class="pc-borrador" role="status">
      <span class="material-symbols-outlined">edit_note</span>
      <span class="pc-borrador-texto">
        <strong>Página en preparación.</strong> No aparece en el menú ni en Google hasta que se publique
        (<code>src/contenido/config.js</code>).
      </span>
    </p>

    <!-- Cabecera -->
    <header class="pc-hero" :class="{ 'has-img': !!contenido.imagen }">
      <div class="pc-hero-text">
        <p class="sd-eyebrow">{{ contenido.eyebrow }}</p>
        <h1 class="sd-title pc-title">{{ contenido.titulo }}</h1>
        <p v-if="contenido.entradilla" class="pc-lead">{{ contenido.entradilla }}</p>
        <div v-else class="pc-lead-pendiente" aria-hidden="true">
          <span></span><span></span><span></span>
        </div>
        <botones-compartir
          class="pc-share"
          compacto
          :titulo="contenido.titulo + ' · Parroquia de Santo Domingo'"
          :texto="contenido.entradilla"
          :ruta="$route.path"
          tipo="pagina"
          :id="contenido.clave"
        />
      </div>
      <figure class="pc-hero-media">
        <img v-if="contenido.imagen" :src="urlGrande(contenido.imagen)" :alt="contenido.titulo" />
        <span v-else class="pc-hero-icon material-symbols-outlined" aria-hidden="true">{{ contenido.icono }}</span>
      </figure>
    </header>

    <!-- Datos destacados -->
    <ul v-if="datos.length" class="pc-datos">
      <li v-for="d in datos" :key="d.etiqueta" class="pc-dato">
        <span class="material-symbols-outlined pc-dato-icon" aria-hidden="true">{{ d.icono }}</span>
        <span class="pc-dato-text">
          <small>{{ d.etiqueta }}</small>
          <strong>{{ d.valor }}</strong>
        </span>
      </li>
    </ul>

    <div class="pc-layout">
      <!-- Índice -->
      <nav class="pc-indice" aria-label="En esta página">
        <p class="pc-indice-titulo">En esta página</p>
        <ul>
          <li v-for="s in contenido.secciones" :key="s.id">
            <a :href="'#' + s.id" :class="{ 'is-active': activa === s.id }" @click.prevent="irA(s.id)">{{ s.titulo }}</a>
          </li>
        </ul>
      </nav>

      <!-- Secciones -->
      <div class="pc-secciones">
        <section
          v-for="s in contenido.secciones"
          :id="s.id"
          :key="s.id"
          ref="secciones"
          class="pc-seccion"
          :class="{ 'pc-seccion--oracion': s.tipo === 'oracion' }"
        >
          <h2 class="pc-seccion-titulo">
            <span class="material-symbols-outlined" aria-hidden="true">{{ s.icono || "article" }}</span>
            {{ s.titulo }}
          </h2>

          <figure v-if="s.imagen" class="pc-seccion-img">
            <img v-img-fallback :src="urlMiniatura(s.imagen)" :alt="s.titulo" loading="lazy" />
          </figure>

          <!-- Contenido fijo escrito en src/contenido (no viene de usuarios) -->
          <div v-if="s.texto" class="sd-prose pc-texto" v-html="s.texto"></div>
          <div v-else class="pc-pendiente">
            <span class="material-symbols-outlined" aria-hidden="true">hourglass_top</span>
            <span class="pc-pendiente-texto">Contenido en preparación</span>
          </div>
        </section>

        <!-- Obras destacadas -->
        <section v-if="contenido.obras && contenido.obras.length" class="pc-seccion">
          <h2 class="pc-seccion-titulo">
            <span class="material-symbols-outlined" aria-hidden="true">museum</span> Obras destacadas
          </h2>
          <div class="pc-obras">
            <figure v-for="o in contenido.obras" :key="o.titulo" class="pc-obra">
              <img v-if="o.imagen" v-img-fallback :src="urlMiniatura(o.imagen)" :alt="o.titulo" loading="lazy" />
              <figcaption>
                <strong>{{ o.titulo }}</strong>
                <span v-if="o.descripcion" class="pc-obra-desc">{{ o.descripcion }}</span>
              </figcaption>
            </figure>
          </div>
        </section>

        <footer class="pc-pie">
          <router-link
            v-if="contenido.albumId"
            :to="{ name: 'carrete', query: { id: contenido.albumId, title: contenido.titulo.replace(/ /g, '_') } }"
            class="sd-btn"
          >
            <span class="material-symbols-outlined">photo_library</span> Ver fotos
          </router-link>
          <botones-compartir
            etiqueta="Compartir"
            compacto
            :titulo="contenido.titulo + ' · Parroquia de Santo Domingo'"
            :texto="contenido.entradilla"
            :ruta="$route.path"
            tipo="pagina"
            :id="contenido.clave"
          />
        </footer>
      </div>
    </div>
  </article>
</template>

<script>
import BotonesCompartir from "@/components/compartir/BotonesCompartir.vue";
import { urlGrande, urlMiniatura } from "@/directives/media.js";
import { PAGINAS } from "@/contenido/config.js";

/**
 * Plantilla de las páginas de contenido fijo (Historia, San Judas Tadeo…).
 * El texto vive en src/contenido/<pagina>.js.
 */
export default {
  name: "PaginaContenido",
  components: { BotonesCompartir },
  props: {
    contenido: { type: Object, required: true },
  },
  metaInfo() {
    const sitio = this.$root.constantes.nombre_sitio;
    return {
      title: `${this.contenido.titulo} · ${sitio}`,
      meta: [
        { vmid: "description", name: "description", content: this.contenido.entradilla || `${this.contenido.titulo} · ${sitio}` },
        // Mientras no se publique, Google no la indexa
        ...(this.publicada ? [] : [{ vmid: "robots", name: "robots", content: "noindex, nofollow" }]),
      ],
      link: [{ rel: "canonical", href: `${this.$root.constantes.sitio_web}${this.$route.path}` }],
    };
  },
  data() {
    return {
      activa: "",
      observador: null,
    };
  },
  computed: {
    publicada() {
      return !!(PAGINAS[this.contenido.clave] && PAGINAS[this.contenido.clave].publicada);
    },
    datos() {
      return (this.contenido.datos || []).filter((d) => d.valor);
    },
  },
  watch: {
    // Historia y San Judas usan este mismo componente: al cambiar de una a otra
    contenido() {
      this.activa = "";
      window.scrollTo(0, 0);
      this.$nextTick(this.observarSecciones);
    },
  },
  mounted() {
    this.observarSecciones();
  },
  beforeDestroy() {
    if (this.observador) this.observador.disconnect();
  },
  methods: {
    urlGrande,
    urlMiniatura,
    // Marca en el índice la sección que se está leyendo
    observarSecciones() {
      if (this.observador) this.observador.disconnect();
      if (!("IntersectionObserver" in window) || !this.$refs.secciones) return;
      this.observador = new IntersectionObserver(
        (entradas) => {
          const visible = entradas.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
          if (visible) this.activa = visible.target.id;
        },
        { rootMargin: "-120px 0px -60% 0px" }
      );
      this.$refs.secciones.forEach((el) => this.observador.observe(el));
    },
    irA(id) {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    },
  },
};
</script>

<style scoped>
.pc-borrador {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 0 0 28px;
  padding: 14px 18px;
  border: 1px dashed var(--sd-warning);
  border-radius: 14px;
  background: var(--sd-warning-soft);
  font-size: 0.9rem;
  color: var(--sd-text);
}

.pc-borrador .material-symbols-outlined {
  color: var(--sd-warning);
}

.pc-borrador-texto {
  color: var(--sd-text);
}

/* Cabecera */
.pc-hero {
  display: grid;
  grid-template-columns: minmax(0, 1.3fr) minmax(0, 1fr);
  align-items: center;
  gap: 40px;
  margin-bottom: 36px;
}

.pc-title {
  font-size: clamp(2rem, 4.4vw, 3.2rem);
}

.pc-lead {
  margin: 16px 0 0;
  font-size: 1.15rem;
  line-height: 1.65;
  color: var(--sd-muted);
}

.pc-lead-pendiente {
  display: grid;
  gap: 10px;
  margin-top: 20px;
  max-width: 520px;
}

.pc-lead-pendiente span {
  height: 14px;
  border-radius: 7px;
  background: var(--sd-surface-2);
}

.pc-lead-pendiente span:last-child {
  width: 60%;
}

.pc-share {
  margin-top: 24px;
}

.pc-hero-media {
  display: grid;
  place-items: center;
  margin: 0;
  aspect-ratio: 4 / 3;
  overflow: hidden;
  border-radius: 24px;
  background:
    radial-gradient(circle at 30% 20%, var(--sd-gold-soft), transparent 60%),
    linear-gradient(135deg, var(--sd-primary-soft), var(--sd-surface-2));
  box-shadow: var(--sd-shadow);
}

.pc-hero-media img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.pc-hero-icon.material-symbols-outlined {
  font-size: clamp(88px, 12vw, 140px);
  color: var(--sd-gold);
  opacity: 0.85;
}

/* Datos */
.pc-datos {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 200px), 1fr));
  gap: 14px;
  margin: 0 0 48px;
  padding: 0;
  list-style: none;
}

.pc-dato {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 18px;
  border: 1px solid var(--sd-border);
  border-radius: 16px;
  background: var(--sd-surface);
}

.pc-dato-icon.material-symbols-outlined {
  font-size: 26px;
  color: var(--sd-gold);
}

.pc-dato-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
  color: var(--sd-text);
}

.pc-dato-text small {
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--sd-muted);
}

.pc-dato-text strong {
  font-size: 1rem;
  color: var(--sd-text);
}

/* Índice + secciones */
.pc-layout {
  display: grid;
  grid-template-columns: 240px minmax(0, 1fr);
  align-items: start;
  gap: 48px;
}

.pc-indice {
  position: sticky;
  top: 110px;
}

.pc-indice-titulo {
  margin: 0 0 10px;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--sd-muted);
}

.pc-indice ul {
  margin: 0;
  padding: 0;
  border-left: 2px solid var(--sd-border);
  list-style: none;
}

.pc-indice a {
  display: block;
  margin-left: -2px;
  padding: 7px 0 7px 14px;
  border-left: 2px solid transparent;
  font-size: 0.92rem;
  color: var(--sd-muted);
  text-decoration: none;
  transition: color 0.15s, border-color 0.15s;
}

.pc-indice a:hover,
.pc-indice a.is-active {
  border-left-color: var(--sd-gold);
  color: var(--sd-text);
}

.pc-secciones {
  display: grid;
  gap: 44px;
  max-width: 760px;
}

.pc-seccion {
  scroll-margin-top: 120px;
}

.pc-seccion-titulo {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 0 0 16px;
  font-family: var(--sd-font-display);
  font-size: clamp(1.35rem, 2.4vw, 1.75rem);
  font-weight: 700;
  color: var(--sd-text);
}

.pc-seccion-titulo .material-symbols-outlined {
  display: grid;
  flex-shrink: 0;
  place-items: center;
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background: var(--sd-gold-soft);
  font-size: 22px;
  color: var(--sd-gold);
}

.pc-seccion-img {
  margin: 0 0 18px;
  overflow: hidden;
  border-radius: 18px;
}

.pc-seccion-img img {
  display: block;
  width: 100%;
  max-height: 440px;
  object-fit: cover;
}

.pc-pendiente {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 22px;
  border: 1px dashed var(--sd-border);
  border-radius: 16px;
  background: var(--sd-surface-2);
  color: var(--sd-muted);
}

.pc-pendiente .material-symbols-outlined {
  font-size: 22px;
  color: var(--sd-muted);
}

.pc-pendiente-texto {
  font-size: 0.95rem;
  color: var(--sd-muted);
}

/* Oración: estampa */
.pc-seccion--oracion {
  padding: 32px clamp(20px, 4vw, 44px);
  border: 1px solid var(--sd-gold);
  border-radius: 24px;
  background:
    radial-gradient(circle at 50% 0%, var(--sd-gold-soft), transparent 70%),
    var(--sd-surface);
  text-align: center;
}

.pc-seccion--oracion .pc-seccion-titulo {
  flex-direction: column;
  justify-content: center;
}

.pc-seccion--oracion .pc-texto {
  font-family: Georgia, "Times New Roman", serif;
  font-size: 1.12rem;
  font-style: italic;
}

/* Obras */
.pc-obras {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(100%, 220px), 1fr));
  gap: 18px;
}

.pc-obra {
  margin: 0;
  overflow: hidden;
  border: 1px solid var(--sd-border);
  border-radius: 16px;
  background: var(--sd-surface);
}

.pc-obra img {
  display: block;
  width: 100%;
  aspect-ratio: 4 / 3;
  object-fit: cover;
}

.pc-obra figcaption {
  display: grid;
  gap: 4px;
  padding: 14px 16px;
  color: var(--sd-text);
}

.pc-obra-desc {
  font-size: 0.88rem;
  color: var(--sd-muted);
}

.pc-pie {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding-top: 24px;
  border-top: 1px solid var(--sd-border);
}

@media (max-width: 991px) {
  .pc-layout {
    grid-template-columns: minmax(0, 1fr);
    gap: 24px;
  }

  .pc-indice {
    position: static;
    padding: 16px 18px;
    border: 1px solid var(--sd-border);
    border-radius: 16px;
    background: var(--sd-surface);
  }
}

@media (max-width: 767px) {
  .pc-hero {
    grid-template-columns: minmax(0, 1fr);
    gap: 24px;
  }

  .pc-hero-media {
    order: -1;
    aspect-ratio: 16 / 9;
  }
}
</style>
